import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  listAccounts, createAccount, updateAccount, deleteAccount, updateAccountStatus,
} from '../../services/bankingApi';
import { listBranches } from '../../services/tierBranchApi';
import type { BankAccount, BankAccountInput, BankMethod, BankMode, BankStatus } from '../../types/banking';

const METHODS: BankMethod[] = ['UPI', 'IMPS', 'NEFT', 'BANK_TRANSFER', 'CRYPTO'];
const MODES: BankMode[] = ['AUTO', 'MANUAL'];
const STATUSES: BankStatus[] = ['active', 'paused', 'suspended'];
const statusColor: Record<BankStatus, 'success' | 'warning' | 'error'> = {
  active: 'success', paused: 'warning', suspended: 'error',
};

const empty: BankAccountInput = {
  label: '', method: 'UPI', mode: 'MANUAL', coinType: 'INR',
  accountHolderName: '', accountNumber: '', ifscCode: '', upiId: '', bankName: '',
  minAmount: 0, maxAmount: 0, dailyLimit: 0, status: 'active',
};

export default function AccountsTab() {
  const [rows, setRows] = useState<BankAccount[]>([]);
  const [branches, setBranches] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<BankAccountInput>(empty);
  const [qr, setQr] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [accts, brs] = await Promise.all([listAccounts(), listBranches({ limit: 200 }).catch(() => ({ data: [] }))]);
      setRows(accts);
      setBranches(((brs as any).data || []).map((b: any) => ({ id: b.id, name: b.name })));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setQr(null); setError(''); setOpen(true); };
  const openEdit = (a: BankAccount) => {
    setForm({
      label: a.label, method: a.method, mode: a.mode, coinType: a.coinType,
      accountHolderName: a.accountHolderName, accountNumber: a.accountNumber,
      ifscCode: a.ifscCode, upiId: a.upiId, bankName: a.bankName, gateway: a.gateway,
      minAmount: a.minAmount, maxAmount: a.maxAmount, dailyLimit: a.dailyLimit, status: a.status,
      ownerBranchId: a.ownerBranchId ?? null,
    });
    setEditId(a.id); setQr(null); setError(''); setOpen(true);
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      if (editId) await updateAccount(editId, form, qr);
      else await createAccount(form, qr);
      setOpen(false); await load();
    } catch (e: any) { setError(e.message || 'Save failed'); } finally { setSaving(false); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this account?')) return;
    await deleteAccount(id); await load();
  };
  const cycleStatus = async (a: BankAccount) => {
    const next = STATUSES[(STATUSES.indexOf(a.status) + 1) % STATUSES.length];
    await updateAccountStatus(a.id, next); await load();
  };

  const set = (k: keyof BankAccountInput) => (e: any) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={openNew}>Add Account</Button>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            {['Label', 'Method', 'Mode', 'Details', 'Owner', 'Limits', 'Daily', 'Status', ''].map((h) => (
              <TableCell key={h} sx={{ color: 'text.secondary' }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((a) => (
            <TableRow key={a.id} hover>
              <TableCell>{a.label || '-'}</TableCell>
              <TableCell><Chip size="small" label={a.method} /></TableCell>
              <TableCell><Chip size="small" variant="outlined" label={a.mode} /></TableCell>
              <TableCell sx={{ fontSize: 12 }}>
                {a.method === 'UPI' ? a.upiId : `${a.bankName || ''} ${a.accountNumber || ''}`.trim() || '-'}
              </TableCell>
              <TableCell sx={{ fontSize: 12 }}>
                {a.ownerBranchName
                  ? <Chip size="small" color="info" label={a.ownerBranchName} />
                  : <Chip size="small" variant="outlined" label="Global" />}
              </TableCell>
              <TableCell sx={{ fontSize: 12 }}>{a.minAmount || 0} - {a.maxAmount || '∞'}</TableCell>
              <TableCell sx={{ fontSize: 12 }}>{a.dailyLimit ? `${a.dailyUsed}/${a.dailyLimit}` : '∞'}</TableCell>
              <TableCell>
                <Chip size="small" color={statusColor[a.status]} label={a.status}
                  onClick={() => cycleStatus(a)} sx={{ cursor: 'pointer' }} />
              </TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => openEdit(a)}><Pencil size={15} /></IconButton>
                <IconButton size="small" color="error" onClick={() => remove(a.id)}><Trash2 size={15} /></IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!rows.length && (
            <TableRow><TableCell colSpan={9} align="center" sx={{ color: 'text.secondary', py: 3 }}>
              No bank accounts yet.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit' : 'Add'} Bank Account</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Label" size="small" value={form.label} onChange={set('label')} />
            <Stack direction="row" spacing={2}>
              <TextField select fullWidth label="Method" size="small" value={form.method} onChange={set('method')}>
                {METHODS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              <TextField select fullWidth label="Mode" size="small" value={form.mode} onChange={set('mode')}>
                {MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Stack>
            {form.method === 'UPI' ? (
              <TextField label="UPI ID" size="small" value={form.upiId} onChange={set('upiId')} />
            ) : (
              <>
                <TextField label="Bank Name" size="small" value={form.bankName} onChange={set('bankName')} />
                <Stack direction="row" spacing={2}>
                  <TextField fullWidth label="Account Number" size="small" value={form.accountNumber} onChange={set('accountNumber')} />
                  <TextField fullWidth label="IFSC" size="small" value={form.ifscCode} onChange={set('ifscCode')} />
                </Stack>
              </>
            )}
            <TextField label="Account Holder" size="small" value={form.accountHolderName} onChange={set('accountHolderName')} />
            <Stack direction="row" spacing={2}>
              <TextField fullWidth type="number" label="Min Amount" size="small" value={form.minAmount} onChange={set('minAmount')} />
              <TextField fullWidth type="number" label="Max Amount" size="small" value={form.maxAmount} onChange={set('maxAmount')} />
              <TextField fullWidth type="number" label="Daily Limit" size="small" value={form.dailyLimit} onChange={set('dailyLimit')} />
            </Stack>
            <TextField select fullWidth label="Assign to branch" size="small"
              value={form.ownerBranchId ?? ''} onChange={(e) => setForm((f) => ({ ...f, ownerBranchId: e.target.value ? Number(e.target.value) : null }))}
              helperText="Global accounts route via tier pools; a branch-assigned account routes to that branch's users and shows in its portal.">
              <MenuItem value="">Global (no branch)</MenuItem>
              {branches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField select fullWidth label="Status" size="small" value={form.status} onChange={set('status')}>
                {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              <Button component="label" variant="outlined" size="small">
                {qr ? 'QR ✓' : 'QR Image'}
                <input hidden type="file" accept="image/*" onChange={(e) => setQr(e.target.files?.[0] || null)} />
              </Button>
            </Stack>
            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
